import { yupResolver } from '@hookform/resolvers/yup';
import { useMemo } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import * as yup from 'yup';

import {
  EventQuery,
  EventType,
  Milestone,
} from '../../../../apollo/__generated__/graphql';
import { useUpdateMilestoneMutation } from '../../../hooks/useUpdateMilestoneMutation';

export interface EditMilestoneInputs {
  name: string;
  prices: number[];
  noAlcoholDiscount?: number;
  bedroomValue?: number;
  startAt: Date;
  endAt: Date;
  isVoucherOnly: boolean;
}

export const useEditMilestoneDrawer = (
  event: EventQuery['event'],
  milestone: Milestone,
  onClose: () => void,
) => {
  const { updateMilestone } = useUpdateMilestoneMutation(() => {
    onClose();
  });

  const schema = yup
    .object({
      name: yup.string().required('Le nom du palier est requis'),
      prices: yup
        .array()
        .of(yup.number().required('Le prix est requis'))
        .length(event.numberOfDays)
        .required('Le prix est requis'),
      noAlcoholDiscount: yup.number().optional(),
      bedroomValue: yup.number().optional(),
      startAt: yup.date().required('La date de début est requise'),
      endAt: yup.date().required('La date de fin est requise'),
      isVoucherOnly: yup.boolean().required(),
    })
    .required();

  const priceOptions = useMemo(() => {
    return new Array(event.numberOfDays)
      .fill(0)
      .map((_, i) => i)
      .reverse();
  }, [event]);

  const defaultValues = useMemo(() => {
    return {
      name: milestone.name,
      noAlcoholDiscount: milestone.noAlcoholDiscount ?? 0,
      bedroomValue: milestone.bedroomValue ?? 0,
      startAt: milestone.startAt,
      endAt: milestone.endAt,
      isVoucherOnly: milestone.isVoucherOnly,
      prices: milestone.prices.map((price) => price.value),
    };
  }, [milestone, priceOptions]);

  const {
    register,
    handleSubmit,
    setValue,
    getValues,
    formState: { errors },
  } = useForm<EditMilestoneInputs>({
    resolver: yupResolver(schema),
    defaultValues,
  });

  const onValid: SubmitHandler<EditMilestoneInputs> = async (data) => {
    updateMilestone({
      name: data.name,
      noAlcoholDiscount: data.noAlcoholDiscount,
      bedroomValue:
        event.type === EventType.BedroomAndTent ? data.bedroomValue : null,
      startAt: data.startAt,
      endAt: data.endAt,
      isVoucherOnly: data.isVoucherOnly,
      prices: data.prices.filter((price) => price !== undefined),
      milestoneId: milestone.id,
    });
  };

  const onSubmit = handleSubmit(onValid);

  const startAtValue = getValues('startAt');
  const endAtValue = getValues('endAt');

  const onStartAtChange = (date: Date | null) => {
    setValue('startAt', date ?? new Date());
  };

  const onEndAtChange = (date: Date | null) => {
    setValue('endAt', date ?? new Date());
  };

  return {
    onSubmit,
    onStartAtChange,
    onEndAtChange,
    startAtValue,
    endAtValue,
    register,
    errors,
    priceOptions,
  };
};
